// Copyright (c) 2026 杨翔宇 (武汉文理学院)
// G7-H P0（#185 F-A）：runtime projection → store 的 thread view 同步，
// 连同振荡熔断器一起从 chatStore.ts 下沉为本切片（A-9 seam extraction 范式）。

import type { Message } from '@/types';
import type { ChatState } from './chatStore';
import { applyRuntimeProjectionToThreadView } from './chatStore.threadView';
import { selectCurrentThreadId } from './chatStore';
import { applyRuntimeSessionRefresh } from './chatStore.session';
import {
  haveSameMessageShape,
  mergeRuntimeMessagesIntoExisting,
  shouldPreserveRicherMessages,
} from '@/pages/main-chat-runtime/transcript/mainChatTranscriptReconciler';

// ── 振荡熔断 ──
// 恢复路径下投影结果可能在两个表示间翻转（resume 重放同一中断 + run 边界
// trace/duration 注入交替），每次 sync 翻转一次 → useSyncExternalStore 强刷
// → 无限循环（React #185）。守卫 haveSameMessageShape 只挡全等挡不住振荡，
// 这里按 session 记录最近两次 shape 指纹：指纹呈 A↔B 交替即熔断一个静默窗，
// 窗口内跳过写入（吸收振荡；窗口过后放行真实变化）。
const SYNC_OSCILLATION_FLIPS_TO_MUTE = 3;
const SYNC_OSCILLATION_MUTE_MS = 5000;
// F-A v2：写入频率熔断——不依赖模式识别（交替/单调/三态轮转一律拦截）。
// 正常 run 每 sync 写入个位数次；1s 窗口内守卫后的实际写入超过阈值必为循环。
const SYNC_RATE_WINDOW_MS = 1000;
const SYNC_RATE_MAX_WRITES = 12;

type SyncOscillationEntry = {
  lastKey: string;
  prevKey: string | null;
  flips: number;
  mutedUntil: number;
  mutedLogged: boolean;
  /** F-A v2：滑动窗口内的实际写入时间戳（守卫后）。 */
  writeTimes: number[];
  /**
   * D-003：已见过的指纹集合。流式 token 推进每 delta 都改变指纹（单调
   * 前进），原先一律计入 1s 速率窗 → 正常快流也会误熔断。只有「环形
   * 写入」（指纹复现=回到见过的状态）才是 #185 投影↔store 循环的特征，
   * 才应计入速率窗；新指纹=真实推进，不计入。
   */
  seenKeys: Set<string>;
};

const SEEN_KEYS_CAP = 400;

function rememberKey(entry: SyncOscillationEntry, key: string): boolean {
  if (entry.seenKeys.has(key)) return false;
  if (entry.seenKeys.size >= SEEN_KEYS_CAP) entry.seenKeys.clear();
  entry.seenKeys.add(key);
  return true;
}

const syncOscillationState = new Map<string, SyncOscillationEntry>();

function transcriptShapeFingerprint(messages: Message[]): string {
  // 轻量指纹：haveSameMessageShape 关心字段的粗粒度投影（不含正文全文，
  // 只取长度与关键展示位），足以区分「两态交替」与「真实推进」。
  // D-003：ui_blocks/thinking_trace 的 undefined 与空数组在语义上等价
  // （不同投影路径的序列化差异），归一为同值——否则会制造 -1↔0 假翻转。
  return messages
    .map((m) =>
      [
        m.id,
        m.role,
        m.content.length,
        m.thinking_duration_seconds ?? -1,
        m.thinking_trace ? m.thinking_trace.steps.length : 0,
        m.ui_blocks ? m.ui_blocks.length : 0,
      ].join(':')
    )
    .join('|');
}

/** 返回 true = 本次写入处于振荡熔断窗内，应跳过。 */
export function isSyncOscillating(sessionId: string, nextMessages: Message[], now: number): boolean {
  const key = transcriptShapeFingerprint(nextMessages);
  let entry = syncOscillationState.get(sessionId);
  if (!entry) {
    entry = {
      lastKey: key,
      prevKey: null,
      flips: 0,
      mutedUntil: 0,
      mutedLogged: false,
      writeTimes: [],
      seenKeys: new Set(),
    };
    syncOscillationState.set(sessionId, entry);
    rememberKey(entry, key);
    return false;
  }
  if (now < entry.mutedUntil) {
    return true;
  }
  // F-A v2（D-003 修正）：速率窗只计「环形写入」（指纹复现）。单调推进
  // （新指纹）是流式token 的正常节奏，不构成投影↔store 循环。
  const isCircular = !rememberKey(entry, key);
  if (isCircular) {
    entry.writeTimes = entry.writeTimes.filter((t) => now - t < SYNC_RATE_WINDOW_MS);
    entry.writeTimes.push(now);
    // D-003 诊断：环形写入时输出逐消息字段差分（限 DEV），定位翻转字段。
    // 诊断开关：localStorage.d003diag === '1' 时输出环形写入差分（生产可开）。
    if ((typeof localStorage !== 'undefined' && localStorage.getItem('d003diag') === '1') && !entry.mutedLogged) {
      const lastBy = entry.lastKey.split('|').map((s) => s.split(':'));
      const nextBy = nextMessages.map((m) => [
        m.id,
        m.role,
        String(m.content.length),
        String(m.thinking_duration_seconds ?? -1),
        String(m.thinking_trace ? m.thinking_trace.steps.length : 0),
        String(m.ui_blocks ? m.ui_blocks.length : 0),
      ]);
      const diff = nextBy.filter((n, i) => {
        const prev = lastBy[i];
        return !prev || prev.some((v, j) => j > 1 && v !== n[j]);
      });
      if (diff.length) {
        console.warn(
          `[chatStore] sync 环形写入差分（前=${entry.lastKey.slice(0, 80)}）`,
          diff.map((d) => d.join(':')).join(' | ')
        );
      }
    }
  }
  if (entry.writeTimes.length > SYNC_RATE_MAX_WRITES) {
    entry.mutedUntil = now + SYNC_OSCILLATION_MUTE_MS;
    entry.writeTimes = [];
    if (!entry.mutedLogged) {
      entry.mutedLogged = true;
      // D-003：熔断是预期内的防护动作（流式爆发期吸收写入），非应用错误
      // ——error 级会污染错误断言面，降级为 warn（与振荡变体 DEV-warn 对齐）。
      console.warn(
        `[chatStore] sync 写入频率熔断 ${SYNC_OSCILLATION_MUTE_MS}ms（session=${sessionId.slice(0, 8)}…，1s 内实际写入超 ${SYNC_RATE_MAX_WRITES} 次）——疑似投影↔store 循环，请排查 #185 根因`
      );
    }
    return true;
  }
  if (key === entry.lastKey) {
    return false; // 指纹稳定：非振荡（后续 haveSameMessageShape 守卫接管）
  }
  if (entry.prevKey === key) {
    entry.flips += 1; // A↔B 交替
  } else {
    entry.flips = 0; // 单向推进：重置
  }
  entry.prevKey = entry.lastKey;
  entry.lastKey = key;
  if (entry.flips >= SYNC_OSCILLATION_FLIPS_TO_MUTE) {
    entry.mutedUntil = now + SYNC_OSCILLATION_MUTE_MS;
    entry.flips = 0;
    if (!entry.mutedLogged) {
      entry.mutedLogged = true;
      if (import.meta.env.DEV) {
        console.warn(
          `[chatStore] sync 振荡熔断 ${SYNC_OSCILLATION_MUTE_MS}ms（session=${sessionId.slice(0, 8)}…）`
        );
      }
    }
    return true;
  }
  return false;
}

/** @internal 测试专用：清空振荡检测状态。 */
export function resetSyncOscillationStateForTest(): void {
  syncOscillationState.clear();
}

/**
 * runtime projection → store thread view（one-way bridge action）。
 *
 * Writes the computed runtime projection into the store's thread view cache.
 * Guarded against richer stored transcripts and — since G7-H P0 — write
 * oscillation (A↔B flip) via the circuit breaker above. The oscillation guard
 * must stay AFTER the shape guard: real forward progress shares no fingerprint
 * and is never muted.
 */
export function createSyncRuntimeMessagesAction(
  set: (partial: (state: ChatState) => ChatState) => void
): (sessionId: string, messages: Message[]) => void {
  return (sessionId, messages): void => {
    set((state) => {
      const cachedMessages = state.messagesBySession[sessionId] ?? [];
      const currentMessages =
        state.currentSession?.session_id === sessionId ? state.messages : [];
      const mergedMessages = mergeRuntimeMessagesIntoExisting(
        cachedMessages,
        mergeRuntimeMessagesIntoExisting(currentMessages, messages)
      );
      const nextMessages = shouldPreserveRicherMessages(cachedMessages, mergedMessages)
        ? cachedMessages
        : shouldPreserveRicherMessages(currentMessages, mergedMessages)
          ? currentMessages
          : mergedMessages;
      if (
        haveSameMessageShape(cachedMessages, nextMessages) &&
        haveSameMessageShape(currentMessages, nextMessages)
      ) {
        return state;
      }
      if (isSyncOscillating(sessionId, nextMessages, Date.now())) {
        return state;
      }

      const sessionPatch = applyRuntimeSessionRefresh(state, { sessionId, nextMessages });
      const threadId =
        state.currentSession?.session_id === sessionId
          ? selectCurrentThreadId(state)
          : null;
      const nextView =
        state.currentThreadView && threadId
          ? applyRuntimeProjectionToThreadView(state.currentThreadView, {
              sessionId,
              threadId,
              messages: nextMessages,
            })
          : state.currentThreadView;

      return {
        ...state,
        ...sessionPatch,
        currentThreadView: nextView,
        ...(nextView && state.currentSession?.session_id === sessionId
          ? { messages: nextView.messages }
          : {}),
      };
    });
  };
}
